<template>
  <client-only>
    <vc-date-picker
      :model-value="today"
      :attributes="calendarAttributes"
      :locale="locale"
      :first-day-of-week="2"
      :columns="monthColumns"
      :rows="monthRows"
      :step="monthStep"
      :expanded="isFullWidth"
      timezone="Europe/Amsterdam"
      @update:model-value="setDate"
      @update:pages="fromPages"
    >
      <template v-if="showButton" #default="{ togglePopover }">
        <MoleculeButton
          :class="classList"
          :enable-icon-left="enableIconLeft"
          :background-color="backgroundColor"
          :text-color="textColor"
          :size="size"
          :justified="justified"
          :shadowed="shadowed"
          @click="
            () => {
              togglePopover()
              registerGtmDateFilterStart()
            }
          "
        >
          <span v-if="!eventOverviewStore.dateIsSpecified">{{ t_('label') }}</span>
          <span v-else>{{ d_(eventOverviewStore.date, 'short-date') }}</span>
          <AtomIconCalendar
            class="text-app-primary-1-red--fire-brick ml-3 shrink-0"
            :class="[
              {
                'w-3 h-3': iconSize === 3,
                'w-4 h-4': iconSize === 4,
              },
              iconColor,
            ]"
          />
        </MoleculeButton>
      </template>
    </vc-date-picker>
  </client-only>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { toDay } from '~/assets/date'

defineProps({
  backgroundColor: { type: String, required: false, default: '' },
  classList: { type: String, required: false, default: '' },
  enableIconLeft: { type: Boolean, required: false, default: false },
  iconColor: { type: String, required: false, default: '' },
  iconSize: { type: Number, required: false, default: 3 },
  isFullWidth: { type: Boolean, required: false, default: false },
  justified: { type: Boolean, required: false, default: false },
  shadowed: { type: Boolean, required: false, default: false },
  showButton: { type: Boolean, required: false, default: true },
  size: { type: String, required: false, default: 'medium' },
  textColor: { type: String, required: false, default: '' },
  monthColumns: { type: Number, required: false, default: 1 },
  monthRows: { type: Number, required: false, default: 1 },
  monthStep: { type: Number, required: false, default: 1 },
})

const emit = defineEmits(['setDate'])

const { locale } = useI18n()
const eventOverviewStore = useEventOverviewStore()
const registerDateFilterStart = setupGtmDateFilterStart()
const registerDateFilter = setupGtmDateFilter()
const { t_, d_ } = setupTranslationPrefixer('Datepicker')
const daysWithEvents = ref<Date[]>([])
const today = toDay('today')

const calendarAttributes = computed(() => [
  {
    key: 'today',
    highlight: {
      style: {
        backgroundColor: '#D9D9D6',
      },
      contentStyle: {
        color: 'white',
      },
    },
    dates: new Date(),
  },
  {
    // Days with concerts are marked with a dot only. v-calendar v3 reads any non-null `highlight` as
    // "highlight this", falling back to its default solid style for `false`, so the key is left out.
    dot: {
      style: {
        backgroundColor: '#71CC98',
        width: '7px',
        height: '7px',
        'margin-top': '3px',
      },
    },
    dates: daysWithEvents.value,
  },
])

const setDate = (date?: Date) => {
  eventOverviewStore.setDate(date)
  emit('setDate', date)
  if (date) {
    registerDateFilter(date.toString().substring(0, 10))
  }
}

/*
 * v-calendar v3 reports every currently visible month page (not just one "from" page as in v2), which
 * matters when multiple months are shown at once (columns/rows > 1): fetch and merge event days for each
 * of them, not just the first. Unlike v2's page event, this one re-fires on every re-render of the day
 * cells, including the re-render caused by the dots this handler loads, so skip the fetch while the
 * visible months are unchanged to avoid an endless update loop.
 */
let loadedMonths = ''
/** The visible month pages as v-calendar reports them. */
type CalendarPage = { month: number; year: number }

const fromPages = (pages: CalendarPage[]) => {
  const months = pages.map(({ month, year }) => `${year}-${month}`).join(',')
  if (months === loadedMonths) {
    return
  }
  loadedMonths = months

  Promise.all(pages.map(({ month, year }) => eventOverviewStore.fetchMonth({ month, year }))).then((monthsOfEvents) => {
    daysWithEvents.value = monthsOfEvents.flat().map((dateStr: string) => {
      const date = new Date(dateStr)
      date.setHours(0, 0, 0, 0)
      return date
    })
  })
}

const registerGtmDateFilterStart = () => {
  registerDateFilterStart()
}
</script>
